import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, HelpCircle, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import Certificate from "@/components/formations/Certificate";
import {
  AdminCard,
  AdminModal,
  BulkDeleteBar,
  Field,
  SelectCheckbox,
  TableWrap,
  Td,
  Th,
  inputCls,
  textareaCls,
  useBulkDelete,
} from "@/pages/admin/ui";

const EMPTY_FAQ = { question: "", answer: "", topic: "general", order: 0 };

function FaqCard() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FAQ);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/faqs");
      setItems(data);
    } catch (error) {
      toast.error(apiError(error, "Chargement impossible"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bulk = useBulkDelete("/admin/faqs/bulk-delete", load, "question(s)");

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FAQ, order: (items || []).length });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ question: item.question, answer: item.answer, topic: item.topic || "general", order: item.order || 0 });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/faqs/${editing.id}`, form);
        toast.success("Question mise à jour");
      } else {
        await api.post("/admin/faqs", form);
        toast.success("Question ajoutée");
      }
      setOpen(false);
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer la question « ${item.question} » ?`)) return;
    try {
      await api.delete(`/admin/faqs/${item.id}`);
      toast.success("Question supprimée");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    try {
      await Promise.all([
        api.put(`/admin/faqs/${next[index].id}`, { question: next[index].question, answer: next[index].answer, topic: next[index].topic || "general", order: index }),
        api.put(`/admin/faqs/${next[j].id}`, { question: next[j].question, answer: next[j].answer, topic: next[j].topic || "general", order: j }),
      ]);
    } catch (error) {
      toast.error(apiError(error));
      load();
    }
  };

  return (
    <AdminCard
      title="Questions fréquentes (FAQ)"
      description="Questions et réponses affichées sur la page Support"
      data-testid="faq-admin-card"
      action={
        <button
          type="button"
          data-testid="faq-add-btn"
          onClick={openCreate}
          className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-brand-dark"
        >
          <Plus className="h-3.5 w-3.5" /> Ajouter une question
        </button>
      }
    >
      {items === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-xs text-slate-400">Aucune question — ajoutez-en une.</p>
      ) : (
        <>
          <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-faqs" />
          <TableWrap>
            <table className="w-full min-w-[640px]" data-testid="faq-admin-table">
              <thead>
                <tr className="border-b border-line text-left">
                  <Th className="w-9">
                    <SelectCheckbox testId="bulk-all-faqs"
                      checked={items.length > 0 && bulk.selected.length >= items.length}
                      onChange={() => bulk.toggleAll(items.map((f) => f.id))} />
                  </Th>
                  <Th>Question</Th>
                  <Th>Réponse</Th>
                  <Th>Ordre</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id} className="border-b border-line last:border-0" data-testid={`faq-row-${item.id}`}>
                    <Td>
                      <SelectCheckbox testId={`bulk-faq-${item.id}`}
                        checked={bulk.selected.includes(item.id)} onChange={() => bulk.toggle(item.id)} />
                    </Td>
                    <Td className="max-w-[260px] font-semibold"><span className="line-clamp-2">{item.question}</span></Td>
                    <Td className="max-w-[320px] text-slate-500"><span className="line-clamp-2 text-xs">{item.answer}</span></Td>
                    <Td className="text-slate-500">{item.order}</Td>
                    <Td className="text-right">
                      <span className="flex items-center justify-end gap-1.5">
                        <button type="button" data-testid={`faq-up-${index}`} onClick={() => move(index, -1)} disabled={index === 0}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand disabled:opacity-40">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" data-testid={`faq-down-${index}`} onClick={() => move(index, 1)} disabled={index === items.length - 1}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand disabled:opacity-40">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" data-testid={`faq-edit-${item.id}`} onClick={() => openEdit(item)}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" data-testid={`faq-delete-${item.id}`} onClick={() => remove(item)}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title={editing ? "Modifier la question" : "Nouvelle question"}>
        <div className="space-y-4">
          <Field label="Question">
            <input data-testid="faq-form-question" value={form.question}
                   onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))} className={inputCls} />
          </Field>
          <Field label="Réponse">
            <textarea data-testid="faq-form-answer" rows={4} value={form.answer}
                      onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))} className={textareaCls} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Sujet">
              <select data-testid="faq-form-topic" value={form.topic}
                      onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))} className={inputCls}>
                <option value="general">Général</option>
                <option value="commande">Commande</option>
                <option value="activation">Activation</option>
                <option value="paiement">Paiement</option>
                <option value="deblocage">Déblocage & services</option>
                <option value="remboursement">Remboursement</option>
              </select>
            </Field>
            <Field label="Ordre d'affichage">
              <input data-testid="faq-form-order" type="number" min="0" value={form.order}
                     onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))} className={inputCls} />
            </Field>
          </div>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button type="button" onClick={() => setOpen(false)}
                    className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-ink">
              Annuler
            </button>
            <button type="button" data-testid="faq-form-save" onClick={save} disabled={saving}
                    className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
            </button>
          </div>
        </div>
      </AdminModal>
    </AdminCard>
  );
}

function LegalCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/legal-info").then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/legal-info", form);
      setForm(data);
      toast.success("Informations enregistrées");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const setSection = (index, key, value) =>
    setForm((f) => {
      const sections = [...f.sections];
      sections[index] = { ...sections[index], [key]: value };
      return { ...f, sections };
    });

  if (!form) {
    return (
      <AdminCard title="À propos & informations légales" description="Textes affichés en bas de la page Support">
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
      </AdminCard>
    );
  }

  return (
    <AdminCard
      title="À propos & informations légales"
      description="Textes affichés en bas de la page Support"
      data-testid="legal-admin-card"
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Titre de la section">
            <input data-testid="legal-title-input" value={form.about_title}
                   onChange={(e) => setForm((f) => ({ ...f, about_title: e.target.value }))} className={inputCls} />
          </Field>
        </div>
        <Field label="Texte de présentation">
          <textarea data-testid="legal-about-input" rows={3} value={form.about_text}
                    onChange={(e) => setForm((f) => ({ ...f, about_text: e.target.value }))} className={textareaCls} />
        </Field>

        <div className="space-y-3" data-testid="legal-sections-list">
          {form.sections.map((section, index) => (
            <div key={section.id || index} className="rounded-xl border border-line p-4" data-testid={`legal-section-form-${index}`}>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`legal-section-title-${index}`}
                  value={section.title}
                  onChange={(e) => setSection(index, "title", e.target.value)}
                  placeholder="Titre du bloc"
                  className={inputCls}
                />
                <button
                  type="button"
                  data-testid={`legal-section-remove-${index}`}
                  onClick={() => setForm((f) => ({ ...f, sections: f.sections.filter((_, i) => i !== index) }))}
                  title="Supprimer ce bloc"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line text-slate-400 transition-colors duration-150 hover:border-rose-300 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <textarea
                data-testid={`legal-section-text-${index}`}
                rows={3}
                value={section.text}
                onChange={(e) => setSection(index, "text", e.target.value)}
                placeholder="Contenu du bloc"
                className={`${textareaCls} mt-2`}
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          data-testid="legal-section-add"
          onClick={() => setForm((f) => ({ ...f, sections: [...f.sections, { id: "", title: "", text: "" }] }))}
          className="flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-bold text-slate-500 transition-colors duration-150 hover:border-brand hover:text-brand"
        >
          <Plus className="h-3.5 w-3.5" /> Ajouter un bloc
        </button>

        <div className="flex justify-end border-t border-line pt-4">
          <button type="button" data-testid="legal-save-btn" onClick={save} disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-brand-dark disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>
    </AdminCard>
  );
}

const CERT_EMPTY = { issuer_name: "ISMO-TECH", logo: "", signature: "", stamp: "", signatory_name: "ISMO-TECH", signatory_title: "Directeur Général" };

function CertCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/certificate-settings")
      .then(({ data }) => setForm({ ...CERT_EMPTY, ...data }))
      .catch(() => setForm({ ...CERT_EMPTY }));
  }, []);

  if (!form) return <AdminCard title="🎓 Certificat de formation (ISMO-TECH)"><div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div></AdminCard>;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/certificate-settings", form); toast.success("Certificat mis à jour 🎓"); }
    catch (e) { toast.error(apiError(e, "Enregistrement impossible")); } finally { setSaving(false); }
  };

  const preview = {
    certificate_number: "CERT-ISMO-260901-APERCU", user_name: "Nom de l'étudiant",
    formation_title: "Votre formation", issued_at: new Date().toISOString(), issuer_type: "admin",
    issuer_name: form.issuer_name, issuer_logo: form.logo, issuer_signature: form.signature,
    issuer_stamp: form.stamp, signatory_name: form.signatory_name, signatory_title: form.signatory_title,
  };

  return (
    <AdminCard title="🎓 Certificat de formation (ISMO-TECH)"
      description="Branding des certificats des formations du site. Les revendeurs personnalisent le leur depuis leur espace ; le sceau ISMO-TECH reste présent sur tous les certificats.">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Field label="Nom de l'organisme">
            <input data-testid="cert-issuer-name" value={form.issuer_name}
              onChange={(e) => set("issuer_name")(e.target.value)} className={inputCls} />
          </Field>
          <ImageUpload label="Logo (haut du certificat)" value={form.logo} onChange={set("logo")} testId="admin-cert-logo" />
          <ImageUpload label="Signature (image)" value={form.signature} onChange={set("signature")} testId="admin-cert-signature" />
          <ImageUpload label="Cachet / tampon rond" value={form.stamp} onChange={set("stamp")} testId="admin-cert-stamp" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom du signataire">
              <input data-testid="admin-cert-signatory-name" value={form.signatory_name}
                onChange={(e) => set("signatory_name")(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Titre">
              <input data-testid="admin-cert-signatory-title" value={form.signatory_title}
                onChange={(e) => set("signatory_title")(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="flex justify-end border-t border-line pt-4">
            <button type="button" data-testid="admin-cert-save" onClick={save} disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-brand-dark disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
            </button>
          </div>
        </div>
        <div>
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Aperçu en direct</span>
          <div className="overflow-hidden rounded-xl border border-line bg-slate-50 p-3">
            <div style={{ height: 707 * 0.44 }}>
              <div style={{ width: 1000, height: 707, transform: "scale(0.44)", transformOrigin: "top left" }}>
                <Certificate data={preview} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminCard>
  );
}

export default function AdminContent() {
  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-ink">Contenu & FAQ</h1>
        <p className="mt-1 text-sm text-slate-500">
          Modifiez les questions fréquentes et les textes « À propos & informations légales » affichés sur la page Support.
        </p>
      </div>
      <CertCard />
      <FaqCard />
      <LegalCard />
    </div>
  );
}
